import { DemoQuestion } from '../../types/questions';

// Demo questions - 12 engaging questions from first 2 chapters
// Designed to showcase the quality and appeal of the full course!
// Updated structure with individual explanations for each option (expA, expB, expC, expD)
export const demoQuestions: DemoQuestion[] = [
  {
    id: 1,
    text: "Your smartphone can recognize your face to unlock. What type of AI is this?",
    options: [
      "Narrow AI - it's designed for one specific task",
      "General AI - it can do everything humans can do",
      "Super AI - it's smarter than humans at everything",
      "Artificial General Intelligence - it thinks like humans"
    ],
    correct: 0,
    expA: "Exactly! Face recognition is Narrow AI because it's designed for one specific task. It can't write poetry, drive cars, or order pizza (though that would be cool!) - it just recognizes faces really well! This is why testing Narrow AI is different from testing traditional software. 📱✨",
    expB: "Incorrect - General AI doesn't exist yet! We're still working on it. Your phone is smart, but it can't compose symphonies or solve world peace - it just unlocks when it sees your beautiful face! 😊",
    expC: "Too futuristic! Super AI is theoretical and would be smarter than humans at everything. Your phone's face recognition is impressive, but it's not planning world domination - just helping you check Instagram faster! 🤖",
    expD: "Nope, that's the same as General AI! AGI is the holy grail of AI research, but we're not there yet. Your phone just recognizes faces, it doesn't think like humans - though sometimes I wonder if it judges my morning hair! 😅",
    points: 1,
    chapter: "Introduction to AI",
    difficulty: "K1"
  },
  {
    id: 2,
    text: "Which scenario shows the famous 'AI Effect' in action?",
    options: [
      "AI taking over human jobs everywhere (thanks, robots! 🤖)",
      "Chess computers were once 'AI' but now we call them 'just algorithms'",
      "AI becoming self-aware like in sci-fi movies",
      "AI systems getting more expensive as they get smarter"
    ],
    correct: 1,
    expA: "Wrong focus - job displacement isn't the AI Effect! That's a different (and very real) concern. The AI Effect is about perception, not employment. Though I get why you might think that - it's definitely an effect of AI! 💼",
    expB: "Exactly! The AI Effect is when yesterday's 'amazing AI' becomes today's 'boring algorithm'. Chess computers were revolutionary AI, now they're just... chess programs! It's like how smartphones went from 'magical' to 'meh, where's my charger?' 🏆➡️💻",
    expC: "Too Hollywood! Self-aware AI isn't the AI Effect - that's more like the 'AI Apocalypse'! The AI Effect is much more subtle - it's about how we perceive intelligence, not about robots taking over. 🎬",
    expD: "Not quite - pricing isn't the AI Effect! Actually, AI often gets cheaper over time (look at your phone!). The AI Effect is about moving goalposts of what we consider 'real' AI. 💰",
    points: 1,
    chapter: "Introduction to AI",
    difficulty: "K1"
  },
  {
    id: 3,
    text: "You're testing a chatbot that gives medical advice. What's your BIGGEST concern?",
    options: [
      "How fast it responds to questions (speed is life!)",
      "Whether it has a nice, user-friendly interface",
      "Whether it gives accurate, unbiased advice that doesn't harm people",
      "How much server memory it uses during conversations"
    ],
    correct: 2,
    expA: "Speed matters, but not the BIGGEST concern! A fast wrong answer about health can be dangerous. Better to wait 5 seconds for accurate advice than get instant misinformation. Speed matters, but safety comes first! ⚡",
    expB: "UI is important for user experience, but not critical for safety! An ugly interface that gives correct medical advice is better than a beautiful one that suggests essential oils for heart attacks. Pretty doesn't save lives! 🎨",
    expC: "Perfect! Medical AI must be accurate and unbiased - people's lives depend on it! A pretty interface won't help if the bot suggests essential oils for a heart attack. This is why AI testing goes beyond functional testing - we test for safety, bias, and ethical concerns! 🏥⚕️",
    expD: "Memory is important for performance, but not the main safety concern! Even if the chatbot uses lots of memory, that's an infrastructure problem. Wrong medical advice is a life-threatening problem! 💾",
    points: 1,
    chapter: "Quality Characteristics for AI-Based Systems",
    difficulty: "K1"
  },
  {
    id: 4,
    text: "An AI hiring system keeps rejecting female candidates. What's the problem?",
    options: [
      "The system needs more powerful GPUs to process applications",
      "The system is running too slowly during peak hours",
      "The user interface is confusing for HR staff",
      "The system has learned bias from historical hiring data"
    ],
    correct: 3,
    expA: "Hardware isn't the issue! More GPUs won't fix bias - they'll just make biased decisions faster! It's like buying a faster car when the problem is you're driving in the wrong direction. 🚗💨",
    expB: "Speed isn't the issue here! A slow but fair system is better than a fast biased one. You can fix slow with better infrastructure, but bias requires fundamental changes to data and algorithms. ⏱️",
    expC: "UI confusion doesn't explain systematic rejection of female candidates! Even if HR staff are confused, the pattern shows algorithmic bias, not user error. Interface problems don't create gender discrimination. 🖥️",
    expD: "Exactly! AI can learn human biases from training data! If past hiring was biased, AI learns that bias like a parrot repeating bad jokes. This is why testing for fairness is crucial - we don't want AI perpetuating our worst habits! 👩‍💼⚖️",
    points: 1,
    chapter: "Quality Characteristics for AI-Based Systems",
    difficulty: "K2"
  },
  {
    id: 5,
    text: "What makes AI systems fundamentally harder to test than traditional software?",
    options: [
      "AI systems always require more expensive hardware to run",
      "AI systems are probabilistic and can give different outputs for the same input",
      "AI systems are more complex to install and configure",
      "AI systems need special programming languages to work"
    ],
    correct: 1,
    expA: "Hardware cost isn't the main testing challenge! Yes, AI can be expensive to run, but that's a resource issue, not a fundamental testing challenge. You can test on smaller datasets or cloud instances. 💰",
    expB: "Exactly! Unlike traditional software that's predictable (like a vending machine), AI is probabilistic - it might give different (but valid) answers each time! Testing needs to handle this uncertainty. It's like asking your friend for restaurant recommendations - you'll get different answers depending on their mood! 🎲",
    expC: "Installation complexity isn't unique to AI! Many traditional enterprise systems are complex to install. The real challenge is the non-deterministic behavior of AI models during testing. 🔧",
    expD: "Programming languages aren't the main problem! You can use Python, R, Java, or many others for AI. The challenge is that AI learns patterns we can't predict, not the language it's written in. 👨‍💻",
    points: 1,
    chapter: "Introduction to AI",
    difficulty: "K2"
  },
  {
    id: 6,
    text: "You're testing a self-driving car AI. What scenario would you DEFINITELY include?",
    options: [
      "Only normal sunny day driving on perfect roads",
      "Only highway driving at consistent speeds",
      "Extreme weather, construction zones, and emergency situations",
      "Only parking lot practice with no obstacles"
    ],
    correct: 2,
    expA: "Too limited! Testing only perfect conditions is like learning to swim in a kiddie pool and then jumping into the ocean. Real driving includes rain, pedestrians, and drivers who think turn signals are optional! ☀️",
    expB: "Not diverse enough! Highway driving is important, but cars need to handle city streets, school zones, and that one intersection where GPS always gets confused. Variety is key for robust AI! 🛣️",
    expC: "Exactly! Self-driving cars must handle EVERYTHING - rain, snow, construction, emergencies, and probably flying shopping bags too! Testing only 'happy path' scenarios is dangerous. Real AI testing requires edge cases, stress scenarios, and safety-critical situations! 🚗🌧️",
    expD: "Too safe! Parking lots are good for basics, but cars need to handle real-world chaos - like pedestrians who text while walking or cyclists who appear out of nowhere. Start safe, but test the chaos! 🅿️",
    points: 2,
    chapter: "Quality Characteristics for AI-Based Systems",
    difficulty: "K3"
  },
  {
    id: 7,
    text: "An AI system works perfectly in testing but fails in production. What's the MOST likely cause?",
    options: [
      "The production server needs more RAM to handle the load",
      "Users are not following the proper usage instructions",
      "The database connections are configured incorrectly",
      "The real-world data distribution differs from the test data"
    ],
    correct: 3,
    expA: "RAM could be an issue, but it usually causes performance issues, not systematic failures. If the AI logic worked in testing, more memory won't fix fundamental data distribution problems. 🖥️",
    expB: "User error is possible, but if the system worked in testing with controlled users, the problem is likely deeper. AI should be robust enough to handle some user variability. Don't blame the users first! 👥",
    expC: "Database config can affect performance, but if the AI worked in testing, database issues would show different symptoms. This sounds more like a data quality/distribution issue. 🗄️",
    expD: "Exactly! This is the classic 'training vs. reality' problem! AI trained on perfect test data might struggle with messy real-world data. It's like learning to drive in a video game and then trying to drive in actual traffic - reality hits hard! 📊➡️🌍",
    points: 2,
    chapter: "Quality Characteristics for AI-Based Systems",
    difficulty: "K3"
  },
  {
    id: 8,
    text: "What's the KEY difference between Machine Learning and traditional programming approaches?",
    options: [
      "Traditional programming follows rules we write; ML learns patterns from data",
      "ML is always much faster than traditional programming",
      "ML only works with numerical data, traditional programming works with text",
      "ML requires special expensive hardware, traditional programming doesn't"
    ],
    correct: 0,
    expA: "Perfect! Traditional programming: We write the rules (like a recipe). ML: We show examples, AI finds the patterns! It's like teaching a child by showing examples vs. giving step-by-step instructions. Both work, but ML finds patterns we didn't even know existed! 🧠📚",
    expB: "Speed isn't the key difference! ML can be faster or slower depending on the task. Sometimes traditional algorithms are faster! The key difference is HOW they solve problems - rules vs. pattern learning. ⚡",
    expC: "Data types aren't the key difference! Both can work with text, numbers, images, etc. ML is actually great with text (like this conversation!), and traditional programming handles numbers just fine. 📊📝",
    expD: "Hardware requirements aren't the defining factor! You can do ML on a laptop or even a phone. Traditional programming can also need powerful hardware for complex applications. It's about approach, not hardware! 💻",
    points: 1,
    chapter: "Introduction to AI",
    difficulty: "K1"
  },
  {
    id: 9,
    text: "Why is 'explainability' becoming a legal requirement for AI systems?",
    options: [
      "It makes AI systems process data much faster",
      "It helps us understand WHY AI made a decision, especially for important choices",
      "It significantly reduces the cost of AI development",
      "It makes AI systems look more professional to users"
    ],
    correct: 1,
    expA: "Speed isn't the goal of explainability! Actually, adding explainability can slow systems down because they need to generate explanations. The goal is understanding, not speed. ⚡",
    expB: "Exactly! Explainability is crucial for trust, safety, and legal compliance! If AI denies your loan or medical treatment, you deserve to know why. 'Because the black box said so' isn't good enough - we need AI to show its work, just like in math class! 🤔💡",
    expC: "Cost reduction isn't the main goal! Explainability often increases development costs because it adds complexity. But it's worth it for trust and compliance. Think of it as insurance for your AI! 💰",
    expD: "Professional appearance isn't the main reason! Explainability is about legal compliance and trust, not cosmetics. A transparent decision is more important than a pretty interface that hides how decisions are made. ✨",
    points: 1,
    chapter: "Quality Characteristics for AI-Based Systems",
    difficulty: "K2"
  },
  {
    id: 10,
    text: "What's the biggest challenge when testing AI systems for fairness across different demographics?",
    options: [
      "Fairness testing requires specialized expensive hardware",
      "AI systems process data too quickly to monitor for fairness",
      "Defining what 'fair' means can be subjective and context-dependent",
      "Most AI systems are too complex to test for fairness"
    ],
    correct: 2,
    expA: "Hardware isn't the main problem! You can test fairness on regular computers - it's more about statistical analysis than computational power. The real challenge is defining what fair means! 💻",
    expB: "Processing speed isn't the obstacle! We can log decisions and analyze them later. Speed is actually helpful - more data points to analyze for fairness patterns! The challenge is philosophical, not technical. ⚡",
    expC: "Absolutely! Fairness isn't just a technical problem - it's a human values problem! What's 'fair' in hiring might differ from what's 'fair' in lending. It's like asking a room full of people to agree on pizza toppings - context matters and everyone has opinions! ⚖️🤝",
    expD: "Complexity is manageable! We have statistical tools and frameworks for fairness testing. The real challenge isn't the complexity of the system, but the complexity of defining fairness itself. 🧮",
    points: 2,
    chapter: "Quality Characteristics for AI-Based Systems",
    difficulty: "K3"
  },
  {
    id: 11,
    text: "An AI recommendation system creates 'filter bubbles' by showing users only similar content. This demonstrates which AI testing concern?",
    options: [
      "Perfect algorithmic optimization for user preferences",
      "Highly efficient content processing and delivery",
      "Excellent user experience through personalization",
      "Unintended system behavior and ethical implications"
    ],
    correct: 3,
    expA: "Not perfect if it creates problems! Yes, the algorithm is optimized for preferences, but perfect optimization can lead to unintended consequences. Sometimes 'perfect' can be too much of a good thing! 🎯",
    expB: "Efficiency isn't the problem here! The system efficiently delivers content, but efficiency alone isn't enough. We also need to consider the broader impact on users and society. 🚀",
    expC: "User experience might seem excellent, but filter bubbles can actually harm users by limiting their exposure to diverse viewpoints. Sometimes good UX has hidden costs! 😊",
    expD: "Exactly! Filter bubbles show how AI can have unintended consequences! While efficient, this can limit discovery and reinforce biases. It's like having a friend who only recommends action movies because you liked one once - variety is the spice of life! 🔄📱",
    points: 2,
    chapter: "Quality Characteristics for AI-Based Systems",
    difficulty: "K3"
  },
  {
    id: 12,
    text: "What's the ultimate goal of professional AI testing?",
    options: [
      "To ensure AI systems are safe, reliable, and beneficial for users",
      "To make AI systems run as fast as possible on any hardware",
      "To make AI systems as complex and feature-rich as possible",
      "To ensure AI systems can be deployed on mobile devices"
    ],
    correct: 0,
    expA: "Perfect! The ultimate goal is creating AI that helps people safely and reliably! Speed and complexity are nice bonuses, but safety and reliability come first. It's like building a bridge - it needs to be safe before it can be pretty or fast to cross! 🛡️✨",
    expB: "Speed is important, but not the ultimate goal! A fast system that gives wrong answers isn't helpful. Better to have a slightly slower system that you can trust with important decisions. 🏃‍♂️",
    expC: "Complexity for complexity's sake isn't the goal! Sometimes simpler is better. The best AI solves real problems elegantly, not impressively. Feature creep can actually make systems worse! 🔧",
    expD: "Mobile deployment is useful, but it's just one deployment option! The ultimate goal is broader - ensuring AI serves humanity well, whether on mobile, desktop, or in the cloud. 📱",
    points: 1,
    chapter: "Introduction to AI",
    difficulty: "K1"
  }
];